/**
 * RESPONSIBILITY: one band down a ministry's page — chosen from a list of templates.
 *
 * It began as one shape, a picture and the words beside it, and that shape is still what a
 * band is unless somebody picks another: see band-themes.ts. The template is the only
 * field always asked for and it decides every other one, the way an event's band does —
 * pick Gallery and you are asked for pictures and nothing else.
 *
 * Hiding a field never clears it, so changing template and back loses nothing.
 *
 * ROWS, NOT PROSE THAT GETS PARSED. The design this came from produced the same bands by
 * reading the rendered HTML back: finding `<img>` tags with a regex, splitting on `<h2>`,
 * and laying out whatever it found. That put the page's structure inside a paragraph
 * field, where the edit menu cannot describe it and an editor cannot see it. A row says
 * what it is. The picture is an @Img, uploaded and sized like every other picture on the
 * site; the side it sits on is the row's position (or the template, if one says so), which
 * an editor changes by dragging.
 */
import type { ImageValue } from '@framework/core';
import { Row, Text, Paragraph, Img, Bool, Link, Choice, List, RenderOf } from '@framework/core/decorators';
import { BAND_THEMES, BAND_THEME as T, DEFAULT_BAND_THEME } from './band-themes';
import { FaqRow } from '../../events/rows/faq';
import { MinistryPictureRow } from './band-picture';

const WITH_WORDS = [T.Alternating, T.Text, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop];
const WITH_IMAGE = [T.Alternating, T.Image, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop];
/** Everything but the original template, which has never drawn a heading of its own. */
const WITH_HEADING = BAND_THEMES.filter((t) => t !== T.Alternating);

@Row()
export class MinistryBand {
  @Choice({
    label: 'Template', options: BAND_THEMES, default: DEFAULT_BAND_THEME,
    description:
      'Choose this first — it decides what you are asked for. '
      + 'Image and text, alternating: a picture and its words, swapping sides down the page. '
      + 'Text: one box of words. Image: one picture. '
      + 'Image left, text right · Text left, image right: a picture beside words. '
      + 'Image on top, text below · Text on top, image below: one above the other. '
      + 'Questions and answers: a list people can open. '
      + 'Video: a YouTube video. Gallery: a grid of pictures.',
  })
  theme!: string;

  @Text({
    label: 'Heading', maxLength: 300,
    showWhen: { field: 'theme', is: WITH_HEADING },
  })
  heading!: string;

  @Img({
    label: 'Picture', folder: 'ministries', maxSizeMb: 12, size: '1200 × 900',
    showWhen: { field: 'theme', is: WITH_IMAGE },
    description: 'Sits beside the words, or above or below them, as the template says.',
  })
  image!: ImageValue;

  @Paragraph({
    label: 'Words', maxLength: 20000, rows: 6, markdown: true, allow: ['verse'],
    showWhen: { field: 'theme', is: WITH_WORDS },
    description: 'Begin with a heading if the band needs one.',
  })
  body!: string;

  @RenderOf('body')
  bodyHtml!: string;

  @List({
    label: 'Questions', of: () => FaqRow, maxItems: 30,
    showWhen: { field: 'theme', is: [T.Faq] },
  })
  questions!: FaqRow[];

  @Link({
    label: 'YouTube link',
    showWhen: { field: 'theme', is: [T.Video] },
    description: 'Paste the video’s address. It plays on this page.',
  })
  videoHref!: string;

  @List({
    label: 'Pictures', of: () => MinistryPictureRow, maxItems: 40,
    showWhen: { field: 'theme', is: [T.Gallery] },
  })
  pictures!: MinistryPictureRow[];

  /** On unless somebody says otherwise — see commons/shown for why only a no counts. */
  @Bool({
    label: 'Show this band', default: true,
    description: 'Untick to take it off the page without deleting what is in it.',
  })
  show!: boolean;
}
